import React, { FC, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "src/state/delegate";
import {
  connectionManagerSelectors,
  userSelectors,
  viewerSessionSelectors,
} from "src/state/selectors";
import { VoidCallback } from "src/types/common";
import { makePlayerAdapter } from "src/video/engine";

interface TangoPlayerProps {
  className: string;
  listeners: Record<string, VoidCallback>;
  muted: boolean;
  paused: boolean;
  url: string;
}

const selector = (state: RootState) => ({
  streamId: viewerSessionSelectors.getStreamId(state),
  userAccount: userSelectors.getMyAccountId(state),
  userId: connectionManagerSelectors.getUsername(state),
});

const TangoPlayer: FC<TangoPlayerProps> = ({
  url,
  muted,
  paused,
  listeners,
  className,
}) => {
  const canvasRef = useRef(null);
  const [player] = useState(() => makePlayerAdapter(() => canvasRef.current));
  useEffect(
    () => () => {
      player.close();
    },
    [player]
  );

  const { streamId, userAccount, userId } = useSelector(selector, shallowEqual);
  const applicationVersion = window?.twcVersion ?? "N/A";
  const applicationCode = "t";

  const context = {
    applicationVersion,
    applicationCode,
    streamId,
    userAccount,
    userId,
  };

  player.onplaying = listeners.playing;
  player.onpause = listeners.pause;
  player.update(url, muted, paused, context);

  return (
    <>
      {/*
    The base64 data contains a minimal MP3 file with no actual sound
    This is required to fix 'audio won't play in silent mode' issue in iOS Safari
    */}
      <audio
        autoPlay
        muted={muted}
        src="data:audio/mp3;base64,//MkxAAHiAICWABElBeKPL/RANb2w+yiT1g/gTok//lP/W/l3h8QO/OCdCqCW2Cw//MkxAQHkAIWUAhEmAQXWUOFW2dxPu//9mr60ElY5sseQ+xxesmHKtZr7bsqqX2L//MkxAgFwAYiQAhEAC2hq22d3///9FTV6tA36JdgBJoOGgc+7qvqej5Zu7/7uI9l//MkxBQHAAYi8AhEAO193vt9KGOq+6qcT7hhfN5FTInmwk8RkqKImTM55pRQHQSq//MkxBsGkgoIAABHhTACIJLf99nVI///yuW1uBqWfEu7CgNPWGpUadBmZ////4sL//MkxCMHMAH9iABEmAsKioqKigsLCwtVTEFNRTMuOTkuNVVVVVVVVVVVVVVVVVVV//MkxCkECAUYCAAAAFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV"
        style={{ display: "none" }}
      />
      <canvas ref={canvasRef} className={className} width={720} height={1280} />
    </>
  );
};

export default TangoPlayer;
